import { CompetenceItem } from "./Competence";
import { SavedFilter } from "./ConsultantFilter";
import { CustomerSavedFilter } from "./CustomerFilter";
import { JobSavedFilter } from "./JobFilter";
import Metadata from "./Metadata";

interface MetadataName {
	sv?: string;
}

interface NumberSeries {
	customer?: number;
	employment?: number;
	invoice?: number;
	job?: number;
}

export interface DocumentResponse {
	documents: Array<Document>;
	totalCount: number;
}

export interface CompetenceResponse {
	competences: Array<competence>;
}

export interface DocumentInfo {
	branches: Array<string>;
	id: string;
	metadata: {
		created: string;
		createdBy: {
			id: string;
		};
		updated: string;
		updatedBy: {
			id: string;
		};
	};
	mimeType: string;
	organisation: {
		id: string;
	};
	title: string;
	type: string;
	url: string;
}

interface Document {
	id: string;
	mimeType: string;
	title: string;
	type: "document" | "link";
	url: string;
}

interface competence {
	items: Array<any>;
	key: string;
	name: { sv: string };
}

export type InvoiceExportType = "FORTNOX" | "VISMA_NET_BEMANNIA";

export const INVOICE_EXPORT_TYPE_LABELS: Record<InvoiceExportType, string> = {
	FORTNOX: "Fortnox",
	VISMA_NET_BEMANNIA: "Visma.net (Bemannia)",
};

interface Organisation {
	id: string;
	metadata: string;
	name: string;
	orgNo?: string;
	numberSeries: NumberSeries;
	competences: CompetenceItem[];
	availabilityShifts: Shifts[];
	arbetsgivarintyg: {
		apiKey?: string;
		employerId: string;
		enabled: boolean;
		available: boolean;
	};
	/** AI assistant (oo-integration-service). `available` is set by Origo, `enabled` by the org. */
	ai?: {
		enabled: boolean;
		available?: boolean;
	};
	salarySettings: {
		exportType: string;
		cfar?: string;
		/** Which identifier the Hogia exports use as anställningsnummer. Defaults to EMPLOYEE_NUMBER when omitted. */
		employeeIdentifier?: "EMPLOYEE_NUMBER" | "SSN";
		salaryCodes: {
			hourly: string;
			deduction: string;
			sickPay: string;
			sickPayNonPaid: string;
			qualifyingDeduction: string;
			qualifyingDay: string;
			childCare: string;
			leaveOfAbsence: string;
			supplement: string;
			ob1: string;
			ob2: string;
			ob3: string;
			ob4: string;
			ob5: string;
			vacation?: string;
			guaranteedSalary?: string;
			gfl?: string;
			performanceSalary?: string;
		};
	};
	invoiceSettings?: {
		exportType: InvoiceExportType;
		accountNumber: string;
	};
	terms?: Terms;
	theme?: string;
	logoUrl?: string;
	pools?: Pool[];
	defaultContractId?: string;
	contact?: {
		website?: string;
		email?: string;
		phone?: string;
		openHours?: string;
	};
	availabilityReminderCron?: string;
	recruitmentContract?: string;
	address?: {
		address1: string;
		careOf: string;
		postalCode: string;
		city: string;
	};
	dashboardScript?: string;
	denialReasonSettings?: DenialReasonSettings;
	/** Per-org time reporting configuration. Absent = 15 min (the pre-existing behaviour). */
	timeReportSettings?: TimeReportSettings;
	/** Per-org login security policy. Absent = nothing enforced (the pre-existing behaviour). */
	securitySettings?: SecuritySettings;
	/** Org-defined certification taxonomy (admin-managed). */
	certificationTypes?: CertificationType[];
	/** Org-defined consultant tag taxonomy. */
	consultantTagTypes?: ConsultantTagType[];
	/** Org-defined customer tag taxonomy — descriptive labels only. */
	customerTagTypes?: TagType[];
	/** Org-defined job tag taxonomy — descriptive labels only. */
	jobTagTypes?: TagType[];
	/** Requirement rules keyed by competence — gate matching per branch/tjänst/kompetens. */
	competenceRequirements?: CompetenceRequirement[];
	/** Organisation-defined, shared consultant filters. Applied read-only by everyone on the consultant list. */
	consultantFilters?: SavedFilter[];
	/** Organisation-defined, shared customer filters. Applied read-only by everyone on the customer list. */
	customerFilters?: CustomerSavedFilter[];
	/** Organisation-defined, shared job filters. Applied read-only by everyone on the job list. */
	jobFilters?: JobSavedFilter[];
	/**
	 * Org-defined custom fields rendered inside the host cards of the
	 * job / customer / consultant forms (see `placement`). Archived
	 * definitions are kept (values stay readable by integrations) but never
	 * rendered.
	 */
	customFieldDefinitions?: CustomFieldDefinition[];
}

export type CustomFieldEntity = "job" | "customer" | "consultant";
export type CustomFieldType = "string" | "boolean" | "number" | "select" | "date";
/** Stored value. `select` stores the option key, `date` stores "YYYY-MM-DD". */
export type CustomFieldValue = string | number | boolean;
/** Which customer card a CUSTOMER field renders on: "profil" = Grunduppgifter, "ovrigt" = own "Övriga uppgifter" card (Profil tab), "faktura" = Fakturamottagare, "faktura-ovrigt" = own "Övriga faktureringsuppgifter" card (Fakturainformation tab). Absent = "profil". */
export type CustomerFieldPlacement = "profil" | "ovrigt" | "faktura" | "faktura-ovrigt";
/** Which job card a JOB field (or an inherited customer field) renders on. Absent = "ovrigt". */
export type JobFieldPlacement = "bokning" | "ovrigt";
/** Which consultant card a CONSULTANT field renders on. Absent = "ovrigt". */
export type ConsultantFieldPlacement = "grunduppgifter" | "anstallning" | "ovrigt";
/**
 * Host card a field renders inside — a per-entity enum (see the three aliases
 * above). The legacy customer values "general"/"invoice" are rewritten to
 * "profil"/"faktura" by migration; the dashboard only sends the new values.
 */
export type CustomFieldPlacement =
	| CustomerFieldPlacement
	| JobFieldPlacement
	| ConsultantFieldPlacement;
/**
 * Customer fields only: "show" renders the customer's value read-only on the
 * job form, "override" lets the job set its own value. Absent = "none".
 */
export type CustomFieldInheritance = "none" | "show" | "override";
/** Entity-level value map, keyed by `CustomFieldDefinition.key`. */
export type CustomFieldValues = Record<string, CustomFieldValue>;

/** One choice of a `select` field. `key` is the stored value (server slug of the label). */
export interface CustomFieldOption {
	key: string;
	label: { sv: string };
}

export interface CustomFieldDefinition {
	/** Server-generated slug; immutable. */
	key: string;
	/** Which entity form the field belongs to; immutable. */
	entity: CustomFieldEntity;
	/** Value type; immutable (so stored values never become invalid). */
	type: CustomFieldType;
	label: { sv: string };
	description?: { sv: string };
	/** Enforced by the dashboard forms only — the backend never rejects a missing value. */
	required?: boolean;
	/** Applies on the owning entity's create form only. `select`: an option key; `date`: "YYYY-MM-DD". */
	defaultValue?: CustomFieldValue;
	/** `select` only: the choices, in display order. Required and non-empty for that type. */
	options?: CustomFieldOption[];
	/** Hidden from forms; stored values are kept. Set by DELETE when the field is in use. */
	archived?: boolean;
	/** Host card the field renders inside; absent = the entity default (profil / ovrigt / ovrigt). */
	placement?: CustomFieldPlacement;
	/** Customer fields with `inheritance !== "none"`: which job card the field renders inside. Absent = "ovrigt". */
	jobPlacement?: JobFieldPlacement;
	/** Customer fields only; absent = "none". */
	inheritance?: CustomFieldInheritance;
	/**
	 * Customer fields only; absent = false. Members of a koncern get the
	 * koncern's value (merged into their `customFields` on read) and cannot
	 * edit it; non-members edit it like any other field.
	 */
	groupOwned?: boolean;
	/** Sort key within the entity list; absent sorts last (then insertion order). */
	order?: number;
}

/**
 * A job's effective custom-field values (`GET /job/:jobId` only): every
 * inheritable customer field with a value (own override or the customer's)
 * plus every job field with a value.
 */
export type ResolvedCustomFields = Record<
	string,
	{ value: CustomFieldValue; source: "job" | "customer" }
>;

export interface CertificationType {
	key: string;
	name: { sv: string };
	requiresProof?: boolean;
	/** If true, an expiration date is required when assigning; absent/false = never expires (no date input). */
	hasExpirationDate?: boolean;
	/** Validity in days from assignment (used to compute the consultant's expiresAt). */
	validityDuration?: number;
	/** Show certifications of this type in the consultant app. Absent = hidden. */
	visibleToConsultant?: boolean;
	/** Show certifications of this type in list views. Opt-in — absent = hidden in lists. */
	showInListViews?: boolean;
	/**
	 * Remind the consultant by push before certifications of this type expire.
	 * Only meaningful together with `hasExpirationDate`. Absent/false = no reminders.
	 */
	remindBeforeExpiry?: boolean;
	/** Days before expiresAt the reminder is sent. Only read when `remindBeforeExpiry` is true. */
	reminderDaysBeforeExpiry?: number;
}

export interface ConsultantTagType {
	key: string;
	name: { sv: string };
	/** Validity in HOURS from assignment (the consultant's expiresAt is computed automatically). */
	validityDuration?: number;
	/** Optional display color (hex #RRGGBB) used as the tag background. */
	color?: string;
	/** Show tags of this type in list views. Opt-in — absent = hidden in lists. */
	showInListViews?: boolean;
}

/** Customer/job tag type — descriptive label only (no validity). */
export interface TagType {
	key: string;
	name: { sv: string };
	/** Optional display color (hex #RRGGBB) used as the tag background. */
	color?: string;
	/** Show tags of this type in list views. Opt-in — absent = hidden in lists. */
	showInListViews?: boolean;
}

export interface RequirementRef {
	kind: "certification" | "tag";
	key: string;
}

/**
 * Requirement rule — all present scope conditions (competence and/or
 * customer tag) must match for the rule to apply to a job. At least one
 * condition is expected.
 */
export interface CompetenceRequirement {
	branch?: string;
	service?: string;
	skill?: string;
	/** Customer tag key (customerTagTypes) the job's customer must carry. */
	customerTag?: string;
	requires: RequirementRef[];
}

export interface DenialReason {
	key: string;
	name: { sv: string };
}

/** Supported time-report granularities. Modelled as minutes so 1/10/30 can be added later. */
export type TimeReportGranularity = 5 | 15;

/** Applied when an organisation has no `timeReportSettings` — no backfill was done. */
export const DEFAULT_TIME_REPORT_GRANULARITY: TimeReportGranularity = 15;

export interface DenialReasonSettings {
	required: boolean;
	reasons: DenialReason[];
	/** If true, the free-text "Annat" (OTHER) fallback is offered at deny time. Defaults to false. */
	allowOther?: boolean;
}

export interface SecuritySettings {
	/**
	 * When true every internal dashboard user of the organisation must pass SMS
	 * two-factor at login, whatever their own 2FA choice. Enforced by the backend
	 * at login (oo-user-service.get-auth-user), not by this client. Users without
	 * a phone number cannot log in until an admin adds one.
	 */
	requireTwoFactor: boolean;
}

export interface TimeReportSettings {
	/**
	 * Step size, in minutes, of the consultant app's time picker. Governs the app
	 * only — the dashboard's own reported/billed time inputs stay free-text HH:MM
	 * so admins can correct exact times.
	 */
	granularityMinutes: TimeReportGranularity;
}

export default Organisation;

interface Shifts {
	end: string;
	start: string;
}

interface Terms {
	createJobTerms: string;
}

export interface Pool {
	key: string;
	name: string;
	isDefault?: boolean;
	/**
	 * A deactivated pool cannot be assigned to new customers/consultants but
	 * existing references stay valid (and stay filterable in list views).
	 */
	deactivated?: boolean;
}

export interface Branches {
	key: string;
	name: {
		sv: string;
	};
	items: Array<Service>;
}

export interface Service {
	key: string;
	name: {
		sv: string;
	};
	items?: Array<any>;
}
